import { styled } from "styled-components";

import { SectionTitleUnderlined, StyledGrid } from "common/components";
import { coalesceArray, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getCurrentSearchTerm, getTopicSearchIsPending, getTopicSearchResults } from "domains/search/selectors";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";
import { createTopicsSelector } from "entities/topics/selectors";
import type { Topic } from "entities/topics/types";

const StyledContainer = styled.div`
  padding-bottom: 2rem;
`;

export const TopicResults = () => {
  const topics = useMemoisedSelector(getTopicSearchResults, createTopicsSelector, coalesceArray);
  const isPending = useSelector(getTopicSearchIsPending);
  const currentSearchTerm = useSelector(getCurrentSearchTerm);
  const track = useTrack();
  const translate = useTranslate();

  const title = !currentSearchTerm ? "app.search.results.title" : "app.discover.topics.title";

  if (topics.length === 0) {
    return null;
  }

  const onClick = (topic: Topic) => () =>
    track({
      entityId: topic.id,
      entityName: topic.name,
      entityType: "Topic",
      name: "Browse page",
      page: "Search",
      subpage: "Topic detail",
    });

  return (
    <StyledContainer>
      <SectionTitleUnderlined isLoading={isPending} id="topics">
        {translate(title)}
      </SectionTitleUnderlined>
      <StyledGrid numberOfItems={[2, 3, 4, 4, 5]}>
        {topics.slice(0, 8).map((topic) => (
          <TopicTile
            key={topic.id}
            topicId={topic.id}
            topicName={topic.name}
            provider={topic.provider}
            topicBack={{ term: "app.search.title" }}
            attachment={topic.attachment ?? undefined}
            canEdit={topic.can_edit ?? false}
            isCustom={topic.is_custom ?? false}
            isNew={topic.is_new ?? false}
            isFollowing={topic.is_following ?? false}
            imageUrl={topic.images?.x_small}
            onClick={onClick(topic)}
          />
        ))}
      </StyledGrid>
    </StyledContainer>
  );
};
